import React, { useState, useRef } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './DailySurveyChart.css';
import EditModal from './EditModal';
import { updateDailyQuestionResponse } from '../../../../services/dailyQuestionService';
import { useTranslation } from 'react-i18next';

const CustomTooltip = ({ active, payload, label, dataKey }) => {
    const { t } = useTranslation();

    if (active && payload && payload.length) {
        const data = payload[0].payload;
        let displayValue;

        switch (dataKey) {
            case 'wakeUpTime':
                displayValue = `${t('dailySurveyChart.wakeUpTimeChart')} : ${data.wakeUpTimeOriginal}`;
                break;
            case 'bedTime':
                displayValue = `${t('dailySurveyChart.bedTimeChart')} : ${data.bedTimeOriginal}`;
                break;
            case 'sleepQuality':
                displayValue = `${t('dailySurveyChart.sleepQualityChart')} : ${
                    data.sleepQuality !== null && data.sleepQuality !== undefined
                        ? data.sleepQuality
                        : t('dailySurveyChart.noDataMessage')
                }`;
                break;
            default:
                displayValue = '';
        }

        return (
            <div className="custom-tooltip">
                <p>{label}</p>
                <p>{displayValue}</p>
                {data.syncedFromSmartwatch && (
                    <p style={{ 
                        fontSize: '12px', 
                        color: '#888', 
                        fontStyle: 'italic',
                        marginTop: '4px' 
                    }}>
                        ⌚ {t('dailySurveyChart.syncedFromSmartwatch')}
                    </p>
                )}
            </div>
        );
    }
    return null;
};

const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (payload.syncedFromSmartwatch) {
        return (
            <g transform={`translate(${cx},${cy})`}>
                <circle r="6" fill={props.fill || "#8884d8"} stroke="#fff" strokeWidth="2" />
                <text 
                    x="0" 
                    y="-10" 
                    fill="#666" 
                    fontSize="10" 
                    textAnchor="middle"
                    fontFamily="Arial, sans-serif"
                >
                    
                </text>
            </g>
        );
    }
    
    return <circle cx={cx} cy={cy} r="4" fill={props.fill || "#8884d8"} />;
};

const DailySurveyChart = ({ responses, noData, onUpdate, idPrefix = '', isAdmin = false }) => {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedData, setSelectedData] = useState(null);

    const parseTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours + minutes / 60;
    };

    // Parse bed time with adjustment for after-midnight times
    // Times from 0:00 to 6:00 are treated as 24:00 to 30:00 for better visualization
    const parseBedTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        let adjustedHours = hours;

        // If bedtime is between midnight and 6 AM, treat it as next day (24+ hours)
        if (hours >= 0 && hours < 6) {
            adjustedHours = hours + 24;
        }

        return adjustedHours + minutes / 60;
    };

    const formattedData = responses.map(response => ({
        date: new Date(response.date).toLocaleDateString(),
        wakeUpTime: parseTime(response.wakeUpTime),
        wakeUpTimeOriginal: response.wakeUpTime,
        bedTime: parseBedTime(response.bedTime),
        bedTimeOriginal: response.bedTime,
        sleepQuality: response.sleepQuality !== undefined ? response.sleepQuality : null,
        syncedFromSmartwatch: response.syncedFromSmartwatch || false,
        id: response._id
    }));

    // Check if we have any sleep quality data
    const hasSleepQualityData = responses.some(r => r.sleepQuality !== undefined && r.sleepQuality !== null);
    const noSleepQualityData = !hasSleepQualityData;

    const handleLineClick = (data) => {
        if (isAdmin) return; // Disable editing for admin users
        setSelectedData(data);
        setIsModalOpen(true);
    };

    // Format bed time for display (convert 24+ hours back to readable format)
    const formatBedTime = (value) => {
        let hour = Math.floor(value);
        const minutes = Math.round((value - hour) * 60);

        // Convert hours > 24 back to 0-23 range for display
        if (hour >= 24) {
            hour = hour - 24;
        }

        return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const handleSave = async (updatedData) => {
        try {
            await updateDailyQuestionResponse(updatedData.id, {
                wakeUpTime: updatedData.wakeUpTime,
                bedTime: updatedData.bedTime,
                sleepQuality: updatedData.sleepQuality,
            });

            if (onUpdate) onUpdate();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error updating data:', error);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            {noData && (
                <div className="no-data-message">
                    <p>{t('dailySurveyChart.noDataMessage')}</p>
                </div>
            )}
            <div className={noData ? 'blurred' : ''}>
                <div
                    className="daily-survey-chart wake-up-time-chart"
                    id={idPrefix ? `${idPrefix}wake-up-time-chart` : undefined}
                    style={{ cursor: isAdmin ? 'default' : 'pointer' }}
                >
                <h4>{t('dailySurveyChart.wakeUpTimeChart')}</h4>
                <ResponsiveContainer width="100%" height={200} >
                    <LineChart data={formattedData} onClick={(e) => handleLineClick(e.activePayload?.[0]?.payload)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip dataKey="wakeUpTime" />} />
                        <Legend />
                        <Line 
                            type="monotone" 
                            dataKey="wakeUpTime" 
                            stroke="#8884d8" 
                            name={t('dailySurveyChart.wakeUpTimeChart')}
                            dot={<CustomDot fill="#8884d8" />}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div
                className="daily-survey-chart bed-time-chart"
                id={idPrefix ? `${idPrefix}bed-time-chart` : undefined}
                style={{ cursor: isAdmin ? 'default' : 'pointer' }}
            >
                <h4>{t('dailySurveyChart.bedTimeChart')}</h4>
                <ResponsiveContainer width="100%" height={200} >
                    <LineChart data={formattedData} onClick={(e) => handleLineClick(e.activePayload?.[0]?.payload)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis
                            domain={[18, 30]}
                            tickFormatter={formatBedTime}
                        />
                        <Tooltip content={<CustomTooltip dataKey="bedTime" />} />
                        <Legend />
                        <Line 
                            type="monotone" 
                            dataKey="bedTime" 
                            stroke="#82ca9d" 
                            name={t('dailySurveyChart.bedTimeChart')}
                            dot={<CustomDot fill="#82ca9d" />}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div
                className="daily-survey-chart sleep-quality-chart"
                id={idPrefix ? `${idPrefix}sleep-quality-chart` : undefined}
                style={{ cursor: isAdmin ? 'default' : 'pointer', position: 'relative' }}
            >
                <h4>{t('dailySurveyChart.sleepQualityChart')}</h4>
                {noSleepQualityData ? (
                    <div style={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        color: '#666',
                        fontWeight: 'bold'
                    }}>
                        {t('dailySurveyChart.noDataMessage')}
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={200} >
                        <LineChart data={formattedData} onClick={(e) => handleLineClick(e.activePayload?.[0]?.payload)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip dataKey="sleepQuality" />} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="sleepQuality"
                                stroke="#ff7300"
                                name={t('dailySurveyChart.sleepQualityChart')}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
                <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    textAlign: 'center', 
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px'
                }}>
                    <span>⌚</span>
                    <span>{t('dailySurveyChart.syncIndicator')}</span>
                </div>
            </div>
            </div>

            <EditModal
                isOpen={isModalOpen}
                data={selectedData}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
            />
        </div>
    );
};

export default DailySurveyChart;